import {
  SignalDisplayMode,
  SignalsDefinition,
} from './types';

export type OperationalSignalStatus = {
  name: string;
  color: string;
  mode: SignalDisplayMode;
};

export type OperationalSignalSlot = {
  name: string;
  previewStatus?: string;
  statuses: OperationalSignalStatus[];
};

export type OperationalSignalFile = {
  metadata: {
    name: string;
    description?: string;
  };
  hardwareRef?: string;
  mqttTopic?: string;
  signals: OperationalSignalSlot[];
};

export function toOperationalSignalFile(
  definition: SignalsDefinition,
): OperationalSignalFile {
  return {
    metadata: {
      name: definition.metadata?.name || '',
      description: definition.metadata?.description || undefined,
    },
    signals: (definition.signals || []).map((slot) => ({
      name: slot.name,
      previewStatus: slot.previewStatus,
      statuses: (slot.statuses || []).map((status) => ({
        name: status.name,
        color: status.color,
        mode: status.mode,
      })),
    })),
  };
}

export function serializeOperationalSignalFile(doc: OperationalSignalFile): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}

function slugifyId(value: string, fallback: string): string {
  const slug = String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return slug || fallback;
}

export function fromOperationalSignalFile(doc: OperationalSignalFile): {
  definition: SignalsDefinition;
  hardwareRef?: string;
} {
  const baseId = slugifyId(doc.metadata?.name || 'signal', 'signal');
  return {
    hardwareRef: doc.hardwareRef,
    definition: {
      metadata: {
        version: '1.0',
        name: doc.metadata?.name || '',
        description: doc.metadata?.description || '',
      },
      mqttTopic: doc.mqttTopic || '',
      signals: (doc.signals || []).map((slot, index) => {
        const slotId = `${baseId}_${index + 1}`;
        const statuses = (slot.statuses || []).map((status, statusIndex) => ({
          id: `${slotId}_${slugifyId(status.name, `status_${statusIndex + 1}`)}`,
          name: status.name,
          color: status.color,
          mode: status.mode,
        }));
        const previewByName = slot.previewStatus
          ? statuses.find((status) => status.name === slot.previewStatus || status.id === slot.previewStatus)?.id
          : undefined;
        return {
          id: slotId,
          name: slot.name,
          previewStatus: previewByName || statuses[0]?.id,
          statuses,
        };
      }),
    },
  };
}

export function isOperationalSignalFile(raw: unknown): raw is OperationalSignalFile {
  if (!raw || typeof raw !== 'object') return false;
  const doc = raw as OperationalSignalFile;
  return !!doc.metadata && Array.isArray(doc.signals);
}

export function parseSignalFile(raw: string): {
  definition: SignalsDefinition;
  hardwareRef?: string;
} {
  const parsed = JSON.parse(String(raw || '{}'));
  if (isOperationalSignalFile(parsed)) {
    return fromOperationalSignalFile(parsed);
  }
  if (parsed && typeof parsed === 'object' && parsed.metadata && Array.isArray(parsed.signals)) {
    const legacy = parsed as SignalsDefinition & { hardwareRef?: string };
    return {
      definition: {
        metadata: legacy.metadata,
        mqttTopic: legacy.mqttTopic,
        signals: legacy.signals,
      },
      hardwareRef: typeof parsed.hardwareRef === 'string' ? parsed.hardwareRef : undefined,
    };
  }
  throw new Error('Invalid signal file JSON');
}
